import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isEnglishRoute } from '../lib/i18n';

export default function ContactForm({
  defaultSubject = '',
  className = '',
  lang: forcedLang,
}) {
  const location = useLocation();
  const isEn = forcedLang === 'en' || (forcedLang ? false : isEnglishRoute(location.pathname));

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: defaultSubject,
    message: '',
    website_url: '', // Mandatory honeypot field
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = isEn
        ? 'Please enter your name'
        : 'Venligst indtast dit navn';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = isEn
        ? 'Please enter your phone number'
        : 'Venligst indtast dit telefonnummer';
    }
    if (!formData.email.trim()) {
      newErrors.email = isEn
        ? 'Please enter your email address'
        : 'Venligst indtast din e-mailadresse';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = isEn
        ? 'Please enter a valid email address'
        : 'Indtast venligst en gyldig e-mailadresse';
    }
    if (!formData.message.trim()) {
      newErrors.message = isEn
        ? 'Please write a message'
        : 'Venligst skriv en besked';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setStatus(null);

    const fallbackSubject = isEn
      ? 'Inquiry from the website'
      : 'Henvendelse fra hjemmesiden';

    try {
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim() || fallbackSubject,
        message: formData.message.trim(),
        website_url: formData.website_url, // Honeypot
      };

      let response;
      try {
        response = await fetch('/_api/webform/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } catch {
        // network or relative error, fallback to absolute URL
      }

      if (!response || (!response.ok && response.status !== 400)) {
        response = await fetch('https://api.dev.auxo.dk/webform/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          subject: '',
          message: '',
          website_url: '',
        });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`bg-white rounded-lg border border-stone-200 p-6 sm:p-8 shadow-sm ${className}`}
    >
      <div className="mb-6">
        <h3 className="text-xl font-bold text-stone-900 tracking-tight">
          {isEn ? 'Send us a message' : 'Send os en besked'}
        </h3>
        <p className="text-sm text-stone-600 mt-1">
          {isEn
            ? 'Fill out the form and one of our advisors will get back to you as soon as possible.'
            : 'Udfyld formularen, så vender en af vores rådgivere tilbage hurtigst muligt.'}
        </p>
      </div>

      {status === 'success' && (
        <div className="mb-6 p-4 rounded-md bg-[#006368]/10 border border-[#006368]/20 text-[#006368] flex items-start gap-3">
          <i className="fa-solid fa-circle-check text-xl mt-0.5" />
          <div>
            <p className="font-semibold">
              {isEn
                ? 'Thank you for your inquiry!'
                : 'Mange tak for din henvendelse!'}
            </p>
            <p className="text-sm text-stone-700 mt-1">
              {isEn
                ? 'We have received your message and will contact you shortly.'
                : 'Vi har modtaget din besked og kontakter dig snarest muligt.'}
            </p>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="mb-6 p-4 rounded-md bg-red-50 border border-red-200 text-red-700 flex items-start gap-3">
          <i className="fa-solid fa-triangle-exclamation text-xl mt-0.5" />
          <div>
            <p className="font-semibold">
              {isEn ? 'An error occurred' : 'Der opstod en fejl'}
            </p>
            <p className="text-sm mt-1">
              {isEn ? (
                <>
                  Unfortunately the message could not be sent. Please call us at{' '}
                  <a
                    href="tel:57526262"
                    className="underline font-bold whitespace-nowrap text-stone-900"
                  >
                    +45 57 52 62 62
                  </a>{' '}
                  or email{' '}
                  <a
                    href="mailto:info@pmborup.dk"
                    className="underline font-bold whitespace-nowrap text-stone-900"
                  >
                    info@pmborup.dk
                  </a>
                  .
                </>
              ) : (
                <>
                  Beskeden kunne desværre ikke sendes. Ring venligst til os på{' '}
                  <a
                    href="tel:57526262"
                    className="underline font-bold whitespace-nowrap text-stone-900"
                  >
                    57 52 62 62
                  </a>{' '}
                  eller skriv til{' '}
                  <a
                    href="mailto:info@pmborup.dk"
                    className="underline font-bold whitespace-nowrap text-stone-900"
                  >
                    info@pmborup.dk
                  </a>
                  .
                </>
              )}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        {/* Anti-spam honeypot - hidden from real humans */}
        <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
          <label htmlFor="website_url">Website URL</label>
          <input
            type="text"
            id="website_url"
            name="website_url"
            value={formData.website_url}
            onChange={handleChange}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {/* Navn / Name */}
        <div>
          <label
            htmlFor="name"
            className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1"
          >
            {isEn ? 'Your name' : 'Dit navn'}{' '}
            <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={isEn ? 'John Smith' : 'Lars Jensen'}
            className={`w-full px-3.5 py-2.5 rounded border text-sm transition focus:outline-none ${
              errors.name
                ? 'border-red-500 focus:border-red-600 bg-red-50/20'
                : 'border-stone-300 focus:border-[#006368] focus:ring-1 focus:ring-[#006368]'
            }`}
          />
          {errors.name && (
            <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
              <i className="fa-solid fa-circle-exclamation text-[10px]" />
              <span>{errors.name}</span>
            </p>
          )}
        </div>

        {/* Grid for Telefon & Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="phone"
              className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1"
            >
              {isEn ? 'Phone number' : 'Telefonnummer'}{' '}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={isEn ? '+45 ...' : '57 52 62 62'}
              className={`w-full px-3.5 py-2.5 rounded border text-sm transition focus:outline-none ${
                errors.phone
                  ? 'border-red-500 focus:border-red-600 bg-red-50/20'
                  : 'border-stone-300 focus:border-[#006368] focus:ring-1 focus:ring-[#006368]'
              }`}
            />
            {errors.phone && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <i className="fa-solid fa-circle-exclamation text-[10px]" />
                <span>{errors.phone}</span>
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1"
            >
              {isEn ? 'Email address' : 'E-mailadresse'}{' '}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={isEn ? 'john@company.com' : 'lars@virksomhed.dk'}
              className={`w-full px-3.5 py-2.5 rounded border text-sm transition focus:outline-none ${
                errors.email
                  ? 'border-red-500 focus:border-red-600 bg-red-50/20'
                  : 'border-stone-300 focus:border-[#006368] focus:ring-1 focus:ring-[#006368]'
              }`}
            />
            {errors.email && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <i className="fa-solid fa-circle-exclamation text-[10px]" />
                <span>{errors.email}</span>
              </p>
            )}
          </div>
        </div>

        {/* Emne / Subject */}
        <div>
          <label
            htmlFor="subject"
            className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1"
          >
            {isEn ? 'Subject (optional)' : 'Emne (valgfrit)'}
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            placeholder={
              isEn
                ? 'e.g. Inquiry regarding central lubrication or presses'
                : 'F.eks. Rådgivning om centralsmøring eller presser'
            }
            className="w-full px-3.5 py-2.5 rounded border border-stone-300 text-sm focus:outline-none focus:border-[#006368] focus:ring-1 focus:ring-[#006368] transition"
          />
        </div>

        {/* Besked / Message */}
        <div>
          <label
            htmlFor="message"
            className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1"
          >
            {isEn ? 'Message' : 'Besked'}{' '}
            <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            value={formData.message}
            onChange={handleChange}
            placeholder={
              isEn
                ? 'Describe your needs, machine specifications, or requirements...'
                : 'Beskriv jeres behov, maskinønsker eller forespørgsel...'
            }
            className={`w-full px-3.5 py-2.5 rounded border text-sm transition focus:outline-none ${
              errors.message
                ? 'border-red-500 focus:border-red-600 bg-red-50/20'
                : 'border-stone-300 focus:border-[#006368] focus:ring-1 focus:ring-[#006368]'
            }`}
          />
          {errors.message && (
            <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
              <i className="fa-solid fa-circle-exclamation text-[10px]" />
              <span>{errors.message}</span>
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3 px-6 rounded bg-[#006368] hover:bg-[#004d51] text-white font-semibold text-sm tracking-wide transition shadow-sm flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <i className="fa-solid fa-circle-notch fa-spin text-sm" />
              <span>{isEn ? 'Sending message...' : 'Sender besked...'}</span>
            </>
          ) : (
            <>
              <span>{isEn ? 'Send message' : 'Send besked'}</span>
              <i className="fa-solid fa-paper-plane text-xs" />
            </>
          )}
        </button>

        <p className="text-[11px] text-stone-500 text-center pt-1">
          {isEn
            ? 'We treat your information confidentially and respond promptly during business hours.'
            : 'Vi behandler dine oplysninger fortroligt og vender hurtigt tilbage.'}
        </p>
      </form>
    </div>
  );
}
